import { Injectable, signal } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { StoredCollectionNames } from '../../models/stored-collection-names';
import { DbStoredMatchStems, MatchStem, StoredMatchStems, DbMatchStem } from '../../models/match-stem';
import { ApiService } from './api/api.service';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface SynthesisS3Resource {
  doc_id: string;
  match_stems?: DbMatchStem[];
}

interface SynthesisS3Response {
  resources?: SynthesisS3Resource[];
}

@Injectable({
  providedIn: 'root'
})
export class MatchStemsService {

  // Time to live, in milliseconds.
  TTL = 7 * 24 * 60 * 60 * 1000; // seven days
  matchStems = signal<MatchStem[] | null | undefined>(undefined);

  constructor(
    private localStorageService: LocalStorageService,
    private apiService: ApiService,
    private http: HttpClient
  ) { }

  /**
   * Method to store a StoreMatchStem inside localStorage with a TTL (time to live) list of storedContexts.
   * @param documentId document id
   * @param matchStems document list of matchStems to store
   */
  setMatchStemsWithExpiry(documentId: string, matchStems: MatchStem[]): void {
    const itemToStore = {
      'id': documentId,
      'match_stems': matchStems,
      'expiry': Date.now() + this.TTL
    };
    console.log(`item of id ${itemToStore.id} set to stem store`);
    this.localStorageService.storeItem(StoredCollectionNames.STORED_MATCH_STEMS, itemToStore);
  }

  /** Méthode to retrieve all StoredMatchStems from localStorage */
  getStoredMatchStems(): StoredMatchStems[] {
    const storedMatchStems = localStorage.getItem(StoredCollectionNames.STORED_MATCH_STEMS)
    if(!storedMatchStems) return [];
    return JSON.parse(storedMatchStems).storedItems
        .map((dbStoredMatchStems: DbStoredMatchStems) => {
          return new StoredMatchStems(dbStoredMatchStems);
        });
  }

  /** Method retrieving stored MatchStems for a unique document */
  getDocumentStoredMatchStems(documentId: string): MatchStem[] | undefined {
    const storedMatchStems = this.getStoredMatchStems();
    const documentMatchStems = storedMatchStems.find(item => item?.id === documentId);

    return documentMatchStems?.matchStems;
  }

  /** Generic method retrieving matchStems  */
  async initMatchStems(currentNavigationState: { [k: string]: any; } | undefined, documentId: string, openSource: string, openSourceId: string, collectiveOrderId: number, exportId: number, taskId?: string) {
    // Current navigation && openSourceId unused at the moment
    let matchStems: MatchStem[] | null = null;

    const storedDocumentMatchStems = this.getDocumentStoredMatchStems(documentId);

    // Check taskId first (for synthesis) - this takes priority over openSource
    if (taskId && taskId.trim() !== '') {
      // Case synthesis - retrieve match_stems from S3 smart search results
      console.log('case SYNTHESIS - match stem');
      try {
        matchStems = await this.retrieveDocumentMatchStemsFromSynthesisS3(taskId, documentId);
        // Store match_stems for future use (consistent with web search behavior)
        if (matchStems && matchStems.length > 0) {
          this.setMatchStemsWithExpiry(documentId, matchStems);
        }
      } catch (error) {
        console.error('Error in synthesis match stems retrieval:', error);
        matchStems = null;
      }
    } else if (openSource === 'digest') {
      console.log('case DIGEST - match stem');
      // getting digest results s3's url for loading on next step
      const s3DeduplicatedFileUrl = await this.retrieveS3DeduplicatedDigestResultsFileUrlFromCollectiveOrderId(collectiveOrderId);
      // loading digest results document and retrieving match Stems from it
      matchStems = await this.retrieveDocumentMatchStemsFromS3(s3DeduplicatedFileUrl, documentId);
    } else if (openSource === 'share') {
      console.log('case SHARE - match stem');
      const sharedDocument = await this.retrieveSharedDocument(openSourceId);
      matchStems = sharedDocument?.matchStems ?? null;
    } else if (openSource === 'export') {
      console.log('case EXPORT - match stem');
      matchStems = await this.retrieveExportDocumentMatchStems(exportId);
    } else if (storedDocumentMatchStems) {
      console.log('default case STORE - match stem');
      matchStems = storedDocumentMatchStems;
    }
    this.matchStems.set(matchStems);
    return matchStems;
  }

  /** Async call to api to retrieve export's Match Stems */
  async retrieveExportDocumentMatchStems(exportId: number): Promise<MatchStem[] | null> {
    try {
      return await firstValueFrom(this.apiService.exportAdminDoc.getExportDocumentMatchStems(exportId));
    } catch (e) {
      console.log(`error retrieving matchStems from export - ${e}`);
      return null;
    }
  }

  /** Async call to api to retrieve S3DeduplicatedDigestResults file url from a collectiveOrderId */
  async retrieveS3DeduplicatedDigestResultsFileUrlFromCollectiveOrderId(collectiveOrderId: number): Promise<string|undefined|null> {
    try {
      return await firstValueFrom(this.apiService.territoryWatchSending
        .retrieveS3DeduplicatedDigestResultsFileUrlFromCollectiveOrderId(collectiveOrderId));
    } catch (e) {
      console.log(`error on retrieveS3DeduplicatedFileUrlFromCollectiveOrderId - ${e}`);
      return null;
    }
  }

  /** Method to retrieve document's matchStems from its digestResults file */
  async retrieveDocumentMatchStemsFromS3(s3DeduplicatedFileUrl: string | undefined | null, documentId: string): Promise<Array<MatchStem> | null> {
    return await firstValueFrom(this.apiService.territoryWatchSending
      .retrieveDocumentMatchStemsFromS3(s3DeduplicatedFileUrl, documentId))
      .catch((e) => {
        console.log(`error on retrieveDocumentMatchStemsFromS3 - ${e}`);
        return null;
      });
  }

  /** Method to retrieve shared document's matchStems from its id */
  async retrieveSharedDocument(shareId: string) {
    try {
      return await firstValueFrom(this.apiService.share
        .getSharedDocument(shareId));
    } catch (e) {
      console.log(`error on retrieveSharedDocumentMatchStems - ${e}`);
      return null;
    }
  }

  /** Method to retrieve document's matchStems from synthesis S3 JSON file */
  async retrieveDocumentMatchStemsFromSynthesisS3(taskId: string, documentId: string): Promise<MatchStem[] | null> {
    try {
      if (!taskId || !documentId) {
        return null;
      }
      const s3Url = `${environment.s3SmartSearchBaseUrl}/${taskId}.json`;
      const response = await firstValueFrom(this.http.get<SynthesisS3Response>(s3Url));

      if (response?.resources && Array.isArray(response.resources)) {
        // doc_id in S3 may have suffixes like _1, _2, so we need to match by base document ID
        // Find all resources that start with the documentId (to handle suffixes)
        const matchingResources = response.resources.filter((resource: SynthesisS3Resource) =>
          resource.doc_id && resource.doc_id.startsWith(documentId)
        );

        if (matchingResources.length > 0) {
          // If multiple matches, aggregate match_stems from all matching resources
          const stemMap = new Map<string, DbMatchStem>();

          matchingResources.forEach((resource: SynthesisS3Resource) => {
            if (resource.match_stems && Array.isArray(resource.match_stems)) {
              resource.match_stems.forEach((stemData: DbMatchStem) => {
                const existingStem = stemMap.get(stemData.stem);
                if (existingStem) {
                  // Aggregate occurrences and items
                  existingStem.occurrences = (existingStem.occurrences || 0) + (stemData.occurrences || 0);
                  // Merge items arrays, avoiding duplicates
                  const existingItems = new Set(existingStem.items || []);
                  (stemData.items || []).forEach((item: string) => existingItems.add(item));
                  existingStem.items = Array.from(existingItems);
                } else {
                  // Create a copy of the stem data
                  stemMap.set(stemData.stem, {
                    stem: stemData.stem,
                    occurrences: stemData.occurrences || 0,
                    items: [...(stemData.items || [])]
                  });
                }
              });
            }
          });

          // Convert aggregated data to MatchStem instances
          const aggregatedMatchStems = Array.from(stemMap.values()).map((stemData: DbMatchStem) =>
            new MatchStem(stemData)
          );
          return aggregatedMatchStems.length > 0 ? aggregatedMatchStems : null;
        }
      }
      return null;
    } catch (e) {
      console.error(`error on retrieveDocumentMatchStemsFromSynthesisS3 - ${e}`);
      return null;
    }
  }

}
